import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Phone, ChevronDown, Menu, ArrowRight } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { SITE } from "@/data/site";
import { SERVICES, CATEGORY_LABELS } from "@/data/services";
import { LOCATIONS } from "@/data/locations";
import { scrollToGetStarted } from "@/components/CTA";

const Wordmark = () => (
  <Link to="/" data-testid="header-logo" className="group flex flex-col leading-none">
    <span className="font-display text-lg font-bold tracking-tight text-navy transition-colors group-hover:text-navy-800 sm:text-xl">
      Travel Passport <span className="text-gold-dark">&amp;</span> Visa
    </span>
    <span className="mt-1 text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-500">
      LLC — Washington, DC
    </span>
  </Link>
);

const Dropdown = ({ label, testId, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={`flex items-center gap-1.5 py-2 font-display text-sm font-medium tracking-wide transition-colors ${
          open ? "text-gold-dark" : "text-navy hover:text-gold-dark"
        }`}
      >
        {label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <div
        className={`absolute left-1/2 top-full z-50 -translate-x-1/2 pt-2 transition-all duration-200 ${
          open ? "visible opacity-100" : "invisible opacity-0"
        }`}
      >
        <div className="rounded-xl border border-slate-100 bg-white p-2 shadow-xl shadow-navy/10">
          {children}
        </div>
      </div>
    </div>
  );
};

const DropLink = ({ to, children, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className="block whitespace-nowrap rounded-lg px-4 py-2 text-sm text-slate-600 transition-colors hover:bg-gold-pale/50 hover:text-navy"
  >
    {children}
  </Link>
);

const MobileLinks = ({ close }) => (
  <nav className="flex flex-col gap-1 pt-6" data-testid="mobile-nav">
    {[
      { to: "/", label: "Home" },
      { to: "/services", label: "All Services" },
      { to: "/business", label: "Business & Corporate" },
      { to: "/about", label: "About Us" },
      { to: "/faq", label: "FAQ" },
      { to: "/contact", label: "Contact" },
      { to: "/locations", label: "Service Areas" },
    ].map((l) => (
      <Link
        key={l.to}
        to={l.to}
        onClick={close}
        data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        className="rounded-lg px-3 py-3 font-display text-base font-medium text-navy hover:bg-gold-pale/40"
      >
        {l.label}
      </Link>
    ))}
    <div className="mt-4 border-t pt-4">
      <p className="px-3 pb-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Popular Services</p>
      {SERVICES.slice(0, 6).map((s) => (
        <Link
          key={s.slug}
          to={`/services/${s.slug}`}
          onClick={close}
          data-testid={`mobile-nav-service-${s.slug}`}
          className="block rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-gold-pale/40"
        >
          {s.name}
        </Link>
      ))}
    </div>
  </nav>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const categories = ["passport", "visa", "documents", "business"];

  return (
    <header
      className="sticky top-0 z-40 border-b border-slate-100 bg-white/90 backdrop-blur-xl"
      data-testid="site-header"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-4 py-3.5 sm:px-6">
        <Wordmark />
        <nav className="hidden items-center gap-7 lg:flex" data-testid="desktop-nav">
          <Dropdown label="Services" testId="nav-services-dropdown">
            <div className="grid w-[520px] grid-cols-2 gap-1 p-2">
              {categories.map((cat) => (
                <div key={cat}>
                  <p className="px-4 pb-1 pt-2 text-[10px] font-bold uppercase tracking-[0.2em] text-gold-dark">
                    {CATEGORY_LABELS[cat]}
                  </p>
                  {SERVICES.filter((s) => s.category === cat).map((s) => (
                    <DropLink key={s.slug} to={`/services/${s.slug}`} testId={`nav-service-${s.slug}`}>
                      {s.name}
                    </DropLink>
                  ))}
                </div>
              ))}
            </div>
          </Dropdown>
          <Dropdown label="Service Areas" testId="nav-locations-dropdown">
            <div className="grid max-h-[420px] w-[420px] grid-cols-2 gap-1 overflow-y-auto p-2">
              {LOCATIONS.map((l) => (
                <DropLink key={l.slug} to={`/locations/${l.slug}`} testId={`nav-location-${l.slug}`}>
                  {l.name}
                </DropLink>
              ))}
            </div>
          </Dropdown>
          <Link to="/business" data-testid="nav-business" className="font-display text-sm font-medium tracking-wide text-navy transition-colors hover:text-gold-dark">
            Business
          </Link>
          <Link to="/about" data-testid="nav-about" className="font-display text-sm font-medium tracking-wide text-navy transition-colors hover:text-gold-dark">
            About
          </Link>
          <Link to="/faq" data-testid="nav-faq" className="font-display text-sm font-medium tracking-wide text-navy transition-colors hover:text-gold-dark">
            FAQ
          </Link>
          <Link to="/contact" data-testid="nav-contact" className="font-display text-sm font-medium tracking-wide text-navy transition-colors hover:text-gold-dark">
            Contact
          </Link>
        </nav>
        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={SITE.phoneTel}
            data-testid="header-call-now-button"
            className="flex items-center gap-2 rounded-full border border-navy/20 px-5 py-2.5 font-display text-sm font-semibold text-navy transition-colors hover:border-gold hover:text-gold-dark"
          >
            <Phone className="h-4 w-4" />
            {SITE.phoneDisplay}
          </a>
          <button
            type="button"
            data-testid="header-get-started-button"
            onClick={() => scrollToGetStarted(navigate)}
            className="group flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 font-display text-sm font-semibold text-white transition-colors hover:bg-navy-800"
          >
            Get Started
            <ArrowRight className="h-4 w-4 text-gold transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button
              type="button"
              data-testid="mobile-menu-button"
              className="rounded-lg p-2 text-navy lg:hidden"
              aria-label="Open menu"
            >
              <Menu className="h-6 w-6" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[320px] overflow-y-auto bg-white" data-testid="mobile-menu">
            <Wordmark />
            <MobileLinks close={() => setOpen(false)} />
            <a
              href={SITE.phoneTel}
              data-testid="mobile-menu-call-button"
              className="mt-6 flex items-center justify-center gap-2 rounded-full bg-navy py-3.5 font-display text-sm font-semibold text-white"
            >
              <Phone className="h-4 w-4" /> Call {SITE.phoneDisplay}
            </a>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};

export default Header;
